import { useState, useEffect, useCallback } from 'react';
import { Box, HStack, Image, useMediaQuery, Circle, Icon, AspectRatio } from '@chakra-ui/react';

import { IoChevronForward, IoChevronBack } from 'react-icons/io5';

import * as fp from 'lodash/fp';

import { times } from 'lodash';

import DefaultBanner from '~/assets/images/banner.png';

function PlaceHolder() {
  return (
    <AspectRatio flex="1" ratio={3.5 / 1}>
      <Image src={DefaultBanner} borderRadius="4px" />
    </AspectRatio>
  );
}

const matchDisplayCountFn = fp.cond<{ isLargerThan768: boolean; isLargerThan900: boolean }, number>(
  [
    [fp.matches({ isLargerThan900: true }), () => fp.identity(3)],
    [fp.matches({ isLargerThan768: true }), () => fp.identity(2)],
    [fp.stubTrue, () => fp.identity(1)],
  ],
);

type ActivityCarouselProps = {
  images: Array<string>;
  carouselSeconds: number;
};

export default function ActivityCarousel({ images, carouselSeconds }: ActivityCarouselProps) {
  const [isLargerThan900, isLargerThan768] = useMediaQuery([
    '(min-width: 900px)',
    '(min-width:768px)',
  ]);

  const [displayCount, setDisplayCount] = useState(1);
  const [currentPosition, setCurrentPosition] = useState(0);

  const handleResetCorrectPosition = (count: number, position: number): number => {
    return Math.floor(position / count) * count;
  };

  const handleNextPosition = useCallback(() => {
    if (images.length < displayCount) {
      return;
    }
    setCurrentPosition(currentPosition + 1 > images.length - 1 ? 0 : currentPosition + 1);
  }, [currentPosition, images.length, displayCount]);

  const handlePrevPosition = useCallback(() => {
    if (images.length < displayCount) {
      return;
    }
    if (currentPosition - 1 < 0) {
      const resetPosition = images.length - displayCount + 1;
      setCurrentPosition(resetPosition < 0 ? 0 : resetPosition);
      return;
    }

    setCurrentPosition(currentPosition - 1);
  }, [currentPosition, displayCount, images.length]);

  useEffect(() => {
    const updateDisplayCount = matchDisplayCountFn({
      isLargerThan768,
      isLargerThan900,
    });

    setDisplayCount(updateDisplayCount);
    setCurrentPosition((prev) => handleResetCorrectPosition(updateDisplayCount, prev));
  }, [isLargerThan768, isLargerThan900]);

  useEffect(() => {
    let displayLoop: NodeJS.Timeout | null = null;
    if (displayCount < images.length) {
      displayLoop = setInterval(() => {
        handleNextPosition();
      }, carouselSeconds * 1000);
    }
    return () => {
      displayLoop && clearInterval(displayLoop);
    };
  }, [displayCount, images.length, carouselSeconds, handleNextPosition]);

  return (
    <Box m="auto" w="full">
      <HStack pos="relative" spacing={0}>
        <Circle
          as="button"
          onClick={handlePrevPosition}
          size="30px"
          pos="absolute"
          left="-15px"
          bgColor="white"
          cursor="pointer"
          boxShadow="0px 0px 5px rgba(0, 0, 0, 0.2);"
          zIndex="1"
        >
          <Icon as={IoChevronBack} boxSize="1.25rem" color="primary" />
        </Circle>
        <HStack justify="space-around" w="full">
          {times(displayCount, (index) => {
            if (images.length < displayCount && index > images.length - 1) {
              return <PlaceHolder key={index} />;
            }

            const imgUrl = fp.nth((currentPosition + index) % images.length)(images) || '';

            return (
              <AspectRatio flex="1" key={imgUrl} ratio={3.5 / 1}>
                <Image borderRadius="4px" loading="lazy" objectFit="cover" src={imgUrl} />
              </AspectRatio>
            );
          })}
        </HStack>
        <Circle
          as="button"
          onClick={handleNextPosition}
          size="30px"
          pos="absolute"
          right="-15px"
          bgColor="white"
          cursor="pointer"
          boxShadow="0px 0px 5px rgba(0, 0, 0, 0.2);"
          zIndex="1"
        >
          <Icon as={IoChevronForward} boxSize="1.25rem" color="primary" />
        </Circle>
      </HStack>
    </Box>
  );
}
